/**
 * Client-to-server commands.
 *
 * §38.1 draws a hard line: there is no gameplay API. These are realtime and
 * short request-response commands, validated with the same Zod schemas in Cloud
 * Mode and Local Event Mode, so both modes accept exactly the same input.
 */

import { z } from "zod";

/** §22.4 — an answer submission stays under 1 KB. */
export const submitAnswerSchema = z.object({
  /** §21.4 — the signed round token issued when the question opened. */
  roundToken: z.string().max(1024),
  /** Typed answer, or null when the player chose a multiple-choice option. */
  text: z.string().max(250).nullable(),
  choiceId: z.string().max(64).nullable(),
  /** §37.2 telemetry, all client-reported and never used for scoring (§37.3). */
  clientSubmittedAt: z.number().int().nullable().default(null),
  firstInputAt: z.number().int().nullable().default(null),
  revealedChoicesAt: z.number().int().nullable().default(null),
  editCount: z.number().int().min(0).max(1000).default(0),
  autoSubmitted: z.boolean().default(false),
  /** §21.3 step 5 / §30.1 — duplicate answer messages are idempotent. */
  idempotencyKey: z.string().max(64),
});

export const joinSessionSchema = z.object({
  sessionCode: z.string().min(1).max(24),
  /** §15.1 — an existing device token resumes the profile and skips onboarding. */
  profileToken: z.string().max(128).nullable().default(null),
});

/** §36.1 — onboarding. Consent is captured before any photo is requested. */
export const createProfileSchema = z.object({
  fullName: z.string().min(1).max(120),
  email: z.string().email().max(200),
  organization: z.string().max(120).nullable().default(null),
  /** §36.4 — an affirmative action, never a pre-checked box. */
  avatarConsent: z.boolean(),
  /** Present only on the consenting path (§36.2). Data URL or base64. */
  photo: z.string().max(8_000_000).nullable().default(null),
  /** §36.5 — the player may reshuffle to a different animal during onboarding. */
  animalShuffleIndex: z.number().int().min(0).max(50).default(0),
  /**
   * §36.5 — the animal the player actually picked, by name.
   *
   * The shuffle index alone could not carry a choice. The server derives the
   * animal from `hash32(profileId) + shuffleIndex`, and the profile id does not
   * exist until the server creates it, so no client could ever predict which
   * animal an index would land on — a player picked a llama on step three and
   * was issued whatever the hash produced. The name is the choice; the index is
   * kept only as the fallback for a client that does not send one.
   */
  avatarAnimal: z.string().max(40).nullable().default(null),
});

/**
 * §36.1 — what a player may change about themselves, in the lobby.
 *
 * There is no `email`, and its absence is the design rather than an omission.
 * `POST /api/profile` refuses to read an address from a body because honouring
 * one would let anyone verify their own inbox and then submit somebody else's,
 * leaving a profile that carries a verified-looking identity nobody proved.
 * This route carries a weaker credential than that one — a profile token, not a
 * proven sign-in — so it is the last place that rule should soften.
 *
 * A body that includes an address is not rejected. `z.object` strips unknown
 * keys, so the address is dropped by the parse and the handler never sees a
 * field it could mishandle. Do not make this `.passthrough()`.
 */
export const editProfileSchema = z.object({
  sessionId: z.string().min(1),
  /** Absent for an avatar-only edit. The display name is re-derived, not sent. */
  fullName: z.string().min(1).max(120).optional(),
  /** Absent leaves the avatar alone; true takes the photo branch, false the animal. */
  avatarConsent: z.boolean().optional(),
  photo: z.string().max(8_000_000).nullable().default(null),
  avatarAnimal: z.string().max(40).nullable().default(null),
});

export const openQuestionSchema = z.object({
  questionId: z.string(),
  /** §8.3 — overrides the game default for this round only. */
  readingWindowSeconds: z.number().int().min(0).max(30).nullable().default(null),
  timeLimitSeconds: z.number().int().min(5).max(60).nullable().default(null),
});

export const facilitatorRoundActionSchema = z.object({
  roundId: z.string(),
  action: z.enum(["start_countdown", "add_time", "close_now", "cancel", "continue_to_board"]),
  /** For add_time only. */
  seconds: z.number().int().min(1).max(60).optional(),
});

/**
 * A facilitator ruling on an answer the evaluator could not classify.
 *
 * Keyed by the normalized answer rather than by a response id, because one
 * ruling settles every player who gave that answer — which is the entire point
 * of grouping the review queue.
 */
export const ruleAnswerSchema = z.object({
  roundId: z.string(),
  /** The Answer Map key, taken verbatim from the review queue. */
  normalizedAnswer: z.string().min(1).max(500),
  /** Unknown is not a ruling — a facilitator resolving an answer picks one of three. */
  opinion: z.enum(["correct", "partial", "incorrect"]),
  /** Optional note, preserved in the judgement history alongside the change. */
  reason: z.string().max(280).nullable().default(null),
});

/**
 * End review with answers still unresolved.
 *
 * Requires an explicit choice for the remainder rather than defaulting quietly:
 * the facilitator is deciding what happens to real players' scores, and that
 * decision should be one they made rather than one they allowed. Choosing
 * `local_estimate` hands the remainder back to local similarity scoring, which
 * is what the system does when review is switched off entirely.
 */
export const finishAnswerReviewSchema = z.object({
  roundId: z.string(),
  remainder: z.enum(["correct", "partial", "incorrect", "local_estimate"]),
});

/** F-052 — a correction creates a reversing or corrective score event. */
export const correctScoreSchema = z.object({
  sessionPlayerId: z.string(),
  delta: z.number().int().min(-100_000).max(100_000),
  reason: z.string().min(1).max(280),
  reversesScoreEventId: z.string().nullable().default(null),
});

export const createSessionSchema = z.object({
  gameVersionId: z.string(),
  title: z.string().min(1).max(160),
  sessionCode: z.string().min(4).max(24).nullable().default(null),
  deploymentProfile: z.enum(["cloud", "local_event"]),
  defaultTimeLimitSeconds: z.number().int().min(5).max(60).nullable().default(null),
  // Zero stays accepted — an imported game may start the clock on selection —
  // even though the setup slider's own floor is three. See DEFAULT_TIMING_CONFIG.
  readingWindowSeconds: z.number().int().min(0).max(30).nullable().default(null),
  questionResultsSeconds: z.number().int().min(1).max(60).nullable().default(null),
  overallStandingsSeconds: z.number().int().min(1).max(60).nullable().default(null),
  aiGradingEnabled: z.boolean().default(false),
  /**
   * Hold results until a facilitator has ruled on every answer the evaluator
   * could not classify.
   *
   * Off by default. It is the right setting for a certification session where a
   * wrong ruling matters more than thirty seconds of pause, and the wrong one
   * for a fast-moving conference game — so it is the facilitator's call per
   * event, not a platform decision.
   */
  answerReviewEnabled: z.boolean().default(false),
  /** F-030 — late joining is an explicit, enforced, visible setting. */
  allowLateJoin: z.boolean().default(true),
  /** F-073 — trims photos, audio, live counters and results detail. */
  lowBandwidthMode: z.boolean().default(false),
  /** §12.5 — hold the results and standings beats until the facilitator advances. */
  manualBeatAdvance: z.boolean().default(false),
  /**
   * §5.1 — whether the projector may play the theme, the stings and the ticks.
   *
   * Defaults to true. A room that wants silence says so; a request that forgot
   * to mention sound is a request for the show as designed.
   */
  soundEnabled: z.boolean().default(true),
  /**
   * Let a player whose code never arrives ask the facilitator to let them in.
   * Approving signs them in as the address they typed; see
   * docs/specs/2026-09-28-facilitator-access-requests-design.md.
   */
  accessRequestsEnabled: z.boolean().default(false),
});

export type SubmitAnswerCommand = z.infer<typeof submitAnswerSchema>;
export type JoinSessionCommand = z.infer<typeof joinSessionSchema>;
export type CreateProfileCommand = z.infer<typeof createProfileSchema>;
export type OpenQuestionCommand = z.infer<typeof openQuestionSchema>;
export type FacilitatorRoundAction = z.infer<typeof facilitatorRoundActionSchema>;
export type RuleAnswerCommand = z.infer<typeof ruleAnswerSchema>;
export type FinishAnswerReviewCommand = z.infer<typeof finishAnswerReviewSchema>;
export type CorrectScoreCommand = z.infer<typeof correctScoreSchema>;
export type CreateSessionCommand = z.infer<typeof createSessionSchema>;
